import {ref, watch} from 'vue'
import {Chains, getChainFromId} from './dapp/config.mjs'
import { getErcBalance, bn, getAllowance, getGasPrice, getNaiveBalance } from './dapp/index.mjs'
import { applyWalletKeyFromProvider, attachEvmInjectProviderListeners } from './dapp/inject.mjs'
import { db, coerceChainId } from './utils.mjs'
import { markInjectWalletTrusted, needsForcedManualSign } from './walletAuth.mjs'
import { isManualDisconnect, isWalletConnectProvider, reconcileWalletType } from './walletLifecycle.mjs'
import {
  consumeChainNavigationPending,
  buildFromChainPageUrl,
  isTronChainPageUrl,
  TRON_CHAIN_ID,
} from './chainNavigation.mjs'
import {
  attachTronWalletListeners,
  detachTronWalletListeners,
  readTronAccountPassive,
  isTronUiContext,
  isTronInjectAvailable,
  resolveTronWalletSessionKey,
} from './dapp/tronInject.mjs'
import { sanitizeMobileWalletBoot } from './appkitCleanup.mjs'
import { walletRail, whenWalletRailReady } from './walletRail.mjs'
import { PortfolioLoader } from './portfolioSync.mjs'
import { setEvmInjectEventHooks } from './evmInjectHooks.mjs'
import { isMetaMaskTronProvider, tryRestoreMetaMaskTron } from './dapp/metamaskTron.mjs'

let walletSyncEpoch = 0

export function bumpWalletSyncEpoch() {
  walletSyncEpoch += 1
  return walletSyncEpoch
}

function canAutoRestoreWallet() {
  return !isManualDisconnect() && !needsForcedManualSign()
}

function applyInjectedAccountIfAllowed(accountAddr, providerVal, chainIdVal, epoch) {
  if (epoch !== walletSyncEpoch || !canAutoRestoreWallet()) return false
  setAccount(accountAddr)
  setProvider(providerVal)
  if (chainIdVal != null) setChainId(chainIdVal)
  markInjectWalletTrusted(accountAddr)
  return true
}

/** Tron 注入钱包：监听 TronLink/TP 账户切换，同步 store（不刷新页面） */
export function bindTronInjectListeners() {
  if (typeof window === 'undefined') return
  if (!isTronPageActive()) return
  if (!canAutoRestoreWallet()) return

  detachTronWalletListeners()
  attachTronWalletListeners({
    onAccountsChanged: (_acc, passive) => {
      if (!canAutoRestoreWallet()) return
      if (provider.value && isMetaMaskTronProvider(provider.value)) {
        applyWalletSession('metamask')
        return
      }
      const savedKey = String(db.get('wallet_key', '') || '').trim()
      if (savedKey === 'metamask') {
        applyWalletSession('metamask')
        return
      }
      const res = passive || readTronAccountPassive(undefined, savedKey || '')
      if (!res?.account) return
      const epoch = walletSyncEpoch
      if (!applyInjectedAccountIfAllowed(res.account, res.provider, res.chainId, epoch)) return
      applyWalletSession(resolveTronWalletSessionKey(savedKey, res.provider))
      db.set('wallet_type', 'inject')
      db.set('topic', '')
    },
    onDisconnect: () => {
      if (!canAutoRestoreWallet()) return
      setAccount('')
      setProvider(null)
      bumpWalletSyncEpoch()
    },
  })
}

export function unbindTronInjectListeners() {
  detachTronWalletListeners()
}
import { humanizeWalletError } from './humanizeWalletError.mjs'
import {
  walletSessionKey,
  applyWalletSession,
  clearWalletSession,
  hydrateWalletSessionFromDb,
  labelForWalletKey,
  WALLET_LABEL_BY_KEY,
} from './walletSession.mjs'

export {
  walletSessionKey,
  applyWalletSession,
  clearWalletSession,
  hydrateWalletSessionFromDb,
  labelForWalletKey,
  WALLET_LABEL_BY_KEY,
}

function buildStore(def){
  const r = ref(def)
  const setR = (v) => r.value = v
  return () => [r, setR]
}

/** 首屏与 URL 对齐，避免 watch 先用默认 usdt 多打一笔目标链 USDT（兑换页只需原生） */
function initialCrossTypeFromLocation() {
  if (typeof window === 'undefined') return 'usdt'
  try {
    const h = (window.location.hash || '').split('?')[0].toLowerCase()
    if (h.includes('swap') || h.includes('buygas')) return 'naive'
    const p = String(window.location.pathname || '').toLowerCase()
    if (p === '/swap' || p.endsWith('/swap')) return 'naive'
  } catch {
    void 0
  }
  return 'usdt'
}

const defFromChainId = (() => {
  if (typeof window === 'undefined') return 56
  if (window.location.search.includes('fromTron')) return 95500
  const saved = Number(db.get('chainId', 56))
  return getChainFromId(saved) ? saved : 56
})()
const defToChainId = (() => {
  if (defFromChainId === 95500) return 56
  const preferred = defFromChainId !== 201022 ? 201022 : 56
  const saved = Number(db.get('defToChainId', 0))
  if (saved && getChainFromId(saved) && saved !== defFromChainId) return saved
  return getChainFromId(preferred) ? preferred : 56
})() 
export const useAccount = buildStore('')
export const useChainId = buildStore(defFromChainId)
export const useFromChain = buildStore(getChainFromId(defFromChainId))
export const useToChain = buildStore(getChainFromId(defToChainId))
export const useFromBalance = buildStore(bn(0))
export const useToBalance = buildStore(bn(0))
export const useToNaiveBalance = buildStore(bn(0))
/** 目标链 gasPrice（wei），字符串与链上 / Web3 一致；未拉到时为 '0' 便于各处以 bn 判断 */
export const useToChainGasPrice = buildStore('0')
/** 源链 gasPrice（wei），跨链入账 FSC 时用于展示源链 GAS */
export const useFromChainGasPrice = buildStore('0')
export const useAllowance = buildStore(null)
export const useExchangeAllowance = buildStore(null)
export const useProvider = buildStore(null)
export const useToBalanceLoading = buildStore(false)
export const useFromBalanceLoading = buildStore(false)
export const useShowMenu = buildStore(false)
export const useShowToast = buildStore(false)
export const useCrossType = buildStore(initialCrossTypeFromLocation())

/** 跨链/兑换完成后通知左侧记录面板刷新（+1 即触发） */
export const recorderRefreshSeq = ref(0)
export function requestRecorderRefresh() {
  recorderRefreshSeq.value += 1
}

/** 左侧交易记录缓存（按地址+类型），切链不丢、组件重挂载可复用 */
export const recorderListCache = new Map()

export function recorderCacheKey(acc, type) {
  return `${String(acc || '').trim().toLowerCase()}:${type}`
}

/** 轻量签名：数据未变则跳过 UI 更新与 loading 条（须含 other_hash，否则到账后「交易成功」不刷新） */
export function recorderListSignature(list) {
  if (!Array.isArray(list) || !list.length) return 'empty'
  const head = list[0]
  const tail = list[list.length - 1]
  const statusBits = list
    .slice(0, 16)
    .map((r) => `${r?.hash || ''}:${r?.other_hash || ''}:${r?.trx_id ? 1 : 0}`)
    .join(';')
  return [
    list.length,
    head?.hash,
    head?.trx_id,
    head?.inserted_at,
    head?.other_hash,
    tail?.hash,
    tail?.trx_id,
    tail?.inserted_at,
    tail?.other_hash,
    statusBits,
  ].join('|')
}

import { LOCALE_IDS, DEFAULT_LOCALE } from './lang/locales.mjs'

let defLang = ''
try {
  defLang = localStorage.getItem('lang') || ''
} catch (_e) {
  void _e
}
const lang = ref((defLang && LOCALE_IDS.includes(defLang)) ? defLang : DEFAULT_LOCALE)
const setLang = (v) => {
  if (!LOCALE_IDS.includes(v)) return
  lang.value = v
  try {
    localStorage.setItem('lang', v)
  } catch (_e) {
    void _e
  }
}
export const useLang = () => [lang, setLang]

const [account, setAccount] = useAccount()
const [fromBalance, setFromBalance] = useFromBalance()
const [toBalance, setToBalance] = useToBalance()
const [toNaiveBalance, setToNaiveBalance] = useToNaiveBalance()
const [chainId, setChainId] = useChainId()
const [fromChain, setFromChain] = useFromChain()
const [toChain, setToChain] = useToChain()
const [fromLoading, setFromLoading] = useFromBalanceLoading()
const [toLoading, setToLoading] = useToBalanceLoading()
const [allowance, setAllowance] = useAllowance()
const [exchangeAllowance, setExchangeAllowance] = useExchangeAllowance()
const [toChainGasPrice, setToChainGasPrice] = useToChainGasPrice()
const [fromChainGasPrice, setFromChainGasPrice] = useFromChainGasPrice()
const [provider, setProvider] = useProvider()
const [crossType] = useCrossType()

let evmInjectListenerProvider = null

function bindEvmInjectListeners(eth) {
  if (typeof window === 'undefined' || !eth || typeof eth.on !== 'function') return
  /* WC provider 依赖内部 relay 监听；removeAllListeners 会导致 eth_sendTransaction 永远到不了手机 */
  if (isWalletConnectProvider(eth)) return
  if (evmInjectListenerProvider === eth) return
  unbindEvmInjectListeners()
  evmInjectListenerProvider = eth
  attachEvmInjectProviderListeners(eth)
}

function unbindEvmInjectListeners() {
  evmInjectListenerProvider = null
}

function onEvmAccountsChanged(accs) {
  if (!canAutoRestoreWallet()) return
  const a = accs?.[0] ? String(accs[0]).trim() : ''
  if (a) {
    setAccount(a)
    markInjectWalletTrusted(a)
    return
  }
  setAccount('')
  setProvider(null)
  bumpWalletSyncEpoch()
}

function onEvmChainChanged(hex) {
  if (!canAutoRestoreWallet()) return
  const cid = coerceChainId(hex, 0)
  if (cid && getChainFromId(cid)) {
    setChainId(cid)
    db.set('chainId', cid)
  }
}

setEvmInjectEventHooks({ onAccountsChanged: onEvmAccountsChanged, onChainChanged: onEvmChainChanged })

const fromPortfolioLoader = new PortfolioLoader({ setLoading: setFromLoading, debounceMs: 120 })
const toPortfolioLoader = new PortfolioLoader({ setLoading: setToLoading, debounceMs: 120 })

function isTronPageActive() {
  if (walletRail.value === 'evm') return false
  if (walletRail.value === 'tron') return true
  const cid = Number(chainId.value || db.get('chainId', 0))
  return isTronUiContext(cid)
}

/**
 * Tron / ?fromTron：进页即通过 TronLink 协议识别注入并回填账户。
 * 与 EVM 不同：即使 store 已有地址，也要刷新 wallet_key（避免残留 metamask 图标）。
 */
export async function syncTronWalletContext() {
  if (typeof window === 'undefined') return
  if (!canAutoRestoreWallet()) return
  if (!isTronPageActive()) return
  if (db.get('wallet_type') === 'wallet_connect_tron') return
  const epoch = walletSyncEpoch
  const savedKey = String(db.get('wallet_key', '') || '').trim()

  try {
    if (provider.value && isMetaMaskTronProvider(provider.value)) {
      applyWalletSession('metamask')
      db.set('wallet_type', 'inject')
      db.set('topic', '')
      return
    }
    if (savedKey === 'metamask') {
      if (String(account.value || '').trim()) {
        applyWalletSession('metamask')
        return
      }
      const restored = await tryRestoreMetaMaskTron()
      if (restored?.account && epoch === walletSyncEpoch) {
        setChainId(restored.chainId)
        setAccount(restored.account)
        setProvider(restored.provider)
        applyWalletSession('metamask')
        db.set('wallet_type', 'inject')
        db.set('topic', '')
        markInjectWalletTrusted(restored.account)
        return
      }
    }

    if (isTronInjectAvailable() && canAutoRestoreWallet() && epoch === walletSyncEpoch) {
      applyWalletSession(resolveTronWalletSessionKey(savedKey, provider.value))
      db.set('wallet_type', 'inject')
      db.set('topic', '')
    }

    for (let attempt = 0; attempt < 12; attempt++) {
      if (!isTronPageActive() || !canAutoRestoreWallet() || epoch !== walletSyncEpoch) return
      const res = readTronAccountPassive(undefined, savedKey && savedKey !== 'metamask' ? savedKey : '')
      if (res?.account) {
        if (!applyInjectedAccountIfAllowed(res.account, res.provider, res.chainId, epoch)) return
        applyWalletSession(resolveTronWalletSessionKey(savedKey, res.provider))
        db.set('wallet_type', 'inject')
        db.set('topic', '')
        bindTronInjectListeners()
        return
      }
      if (attempt < 11) {
        await new Promise((r) => setTimeout(r, 150 + attempt * 80))
      }
    }
  } catch (e) {
    console.warn('[hieswap] syncTronWalletContext', e)
  }
}

/**
 * 刷新后 MetaMask 等已授权但 store 尚未写入：与顶栏、兑换卡片的「已连接」对齐。
 * - eth_accounts 在扩展注入完成前可能短暂为空，故做少量重试。
 * - 仅当仍存在活跃的 WalletConnect provider（且不是浏览器注入节点）时，才跳过静默 eth_accounts，
 *   避免「曾连过 WC、本地残留 topic」在整页刷新后阻止回填扩展钱包，造成顶栏已由 connectInject 写入地址、
 *   而卡片仍认为未连接的分叉。
 */
export async function syncInjectedAccountIfNeeded() {
  if (typeof window === 'undefined') return
  if (!canAutoRestoreWallet()) return
  if (isTronPageActive()) {
    await syncTronWalletContext()
    return
  }
  if (String(account.value || '').trim()) return
  const epoch = walletSyncEpoch
  const wt = db.get('wallet_type', 'inject')
  const topic = String(db.get('topic', '') || '').trim()
  /* EVM WC：须在 reconcileWalletType(eth) 之前恢复，否则 MetaMask 存在时会清掉 topic */
  if (wt === 'wallet_connect' && topic) {
    try {
      const { tryRestoreEvmWalletConnect } = await import('./dapp/walletconnect.mjs')
      const cid = coerceChainId(db.get('chainId', 0) || 56, 56)
      const res = await tryRestoreEvmWalletConnect(cid)
      if (res?.account && canAutoRestoreWallet() && epoch === walletSyncEpoch) {
        setAccount(res.account)
        setProvider(res.provider)
        if (res.chainId) setChainId(res.chainId)
        markInjectWalletTrusted(res.account)
        hydrateWalletSessionFromDb()
        return
      }
    } catch (e) {
      console.warn('[hieswap] syncInjectedAccountIfNeeded evm wc restore', e)
    }
  }
  const eth = window.ethereum || (window.bitkeep && window.bitkeep.ethereum)
  if (eth) {
    reconcileWalletType(provider.value || eth)
  }
  if (String(account.value || '').trim()) return
  if (!eth || !eth.request) return
  /* 注入钱包已有账户时优先回填 store，不因残留 WC topic 跳过（否则卡片顶栏地址与「连接钱包」分叉） */
  if (wt === 'wallet_connect' && topic) {
    const p = provider.value
    if (p && p !== eth && typeof p.request === 'function') {
      try {
        const accs = await eth.request({ method: 'eth_accounts' })
        const a = accs && accs[0] && String(accs[0]).trim()
        if (a && canAutoRestoreWallet() && epoch === walletSyncEpoch) {
          setAccount(a)
          setProvider(eth)
          db.set('wallet_type', 'inject')
          db.set('topic', '')
          try {
            const hex = await eth.request({ method: 'eth_chainId' })
            const n = coerceChainId(hex, 0)
            if (n && getChainFromId(n)) setChainId(n)
          } catch (_e) {
            void _e
          }
          markInjectWalletTrusted(a)
          applyWalletKeyFromProvider(eth)
          return
        }
      } catch (_e) {
        void _e
      }
      return
    }
  }
  for (let attempt = 0; attempt < 10; attempt++) {
    if (String(account.value || '').trim()) return
    if (!canAutoRestoreWallet() || epoch !== walletSyncEpoch) return
    try {
      const accs = await eth.request({ method: 'eth_accounts' })
      const a = accs && accs[0] && String(accs[0]).trim()
      if (a && canAutoRestoreWallet() && epoch === walletSyncEpoch) {
        setAccount(a)
        setProvider(eth)
        try {
          const hex = await eth.request({ method: 'eth_chainId' })
          const n = coerceChainId(hex, 0)
          if (n && getChainFromId(n)) setChainId(n)
        } catch (_e) {
          void _e
        }
        markInjectWalletTrusted(a)
        applyWalletKeyFromProvider(eth)
        return
      }
    } catch (e) {
      console.warn('[hieswap] syncInjectedAccountIfNeeded', e)
    }
    if (attempt < 9) await new Promise((r) => setTimeout(r, 120 + attempt * 90))
  }
}

async function getGasPriceWithRetry(chainId, attempts = 3) {
  if (!chainId) return '0'
  let lastErr
  for (let i = 0; i < attempts; i++) {
    try {
      const g = await getGasPrice(chainId)
      if (g != null && String(g) !== '' && Number(g) > 0) return String(g)
    } catch (e) {
      lastErr = e
    }
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, 280 * (i + 1)))
    }
  }
  if (lastErr) console.warn('[hieswap] getGasPrice failed', chainId, lastErr)
  return '0'
}

async function fetchErcBalanceResilient(chainId, account) {
  if (!chainId || !account) return '0'
  try {
    return await getErcBalance(chainId, account)
  } catch (e1) {
    console.warn('[hieswap] getErcBalance failed, retry all RPC lines', chainId, e1?.message || e1)
    try {
      return await getErcBalance(chainId, account)
    } catch (e2) {
      console.warn('[hieswap] getErcBalance retry failed', chainId, e2?.message || e2)
      return '0'
    }
  }
}

async function fetchNaiveBalanceResilient(chainId, account) {
  if (!chainId || !account) return bn(0)
  try {
    return await getNaiveBalance(chainId, account)
  } catch (e1) {
    console.warn('[hieswap] getNaiveBalance failed, retry all RPC lines', chainId, e1?.message || e1)
    try {
      return await getNaiveBalance(chainId, account)
    } catch (e2) {
      console.warn('[hieswap] getNaiveBalance retry failed', chainId, e2?.message || e2)
      return bn(0)
    }
  }
}

async function refreshFromChainPortfolio(gen, isStale) {
  const fc = fromChain.value
  const cid = fc && fc.chainId
  const acc = account.value || ''
  if (!cid) {
    setFromLoading(false)
    return
  }
  if (!acc) {
    setFromBalance(bn(0))
    setAllowance(null)
    setExchangeAllowance(null)
    setFromLoading(false)
    return
  }
  setFromChainGasPrice('0')
  const balance = await fetchErcBalanceResilient(cid, acc)
  if (isStale() || fromChain.value.chainId !== cid || account.value !== acc) return
  setFromBalance(bn(balance))
  const allowance = await getAllowance(cid, acc).catch(() => '0')
  if (isStale() || fromChain.value.chainId !== cid || account.value !== acc) return
  setAllowance(bn(allowance))
  const exSpender = fc && fc.exchangeContract
  const exchangeAllowance = await getAllowance(cid, acc, exSpender).catch(() => '0')
  if (isStale() || fromChain.value.chainId !== cid || account.value !== acc) return
  setExchangeAllowance(bn(exchangeAllowance))
  if (isStale() || fromChain.value.chainId !== cid) return
  const gasWei = await getGasPriceWithRetry(cid, 3)
  if (!isStale() && fromChain.value.chainId === cid) {
    setFromChainGasPrice(gasWei)
  }
}

async function refreshToChainPortfolio(gen, isStale) {
  const chain = toChain.value
  const cid = chain && chain.chainId
  if (!cid) {
    setToLoading(false)
    return
  }
  setToChainGasPrice('0')
  const acc = account.value || ''
  const naive = crossType.value === 'naive'
  if (naive) {
    const naiveBal = await fetchNaiveBalanceResilient(cid, acc)
    if (isStale() || toChain.value.chainId !== cid) return
    setToBalance(bn(0))
    setToNaiveBalance(naiveBal)
  } else {
    const [balance, naiveBal] = await Promise.all([
      fetchErcBalanceResilient(cid, acc),
      fetchNaiveBalanceResilient(cid, acc),
    ])
    if (isStale() || toChain.value.chainId !== cid) return
    setToBalance(bn(balance))
    setToNaiveBalance(naiveBal)
  }
  if (isStale() || toChain.value.chainId !== cid) return
  const gasWei = await getGasPriceWithRetry(cid, 3)
  if (!isStale() && toChain.value.chainId === cid) {
    setToChainGasPrice(gasWei)
  }
}

function scheduleFromPortfolioRefresh() {
  fromPortfolioLoader.schedule(refreshFromChainPortfolio)
}

function scheduleToPortfolioRefresh() {
  toPortfolioLoader.schedule(refreshToChainPortfolio)
}

watch([fromChain, account], scheduleFromPortfolioRefresh, { immediate: true })
watch([toChain, crossType, account], scheduleToPortfolioRefresh, { immediate: true })
watch(provider, (p) => {
  if (p && typeof p.request === 'function') bindEvmInjectListeners(p)
}, { immediate: true })
whenWalletRailReady({ setChainId, getChainId: () => Number(chainId.value) }).then(() => {
  void syncInjectedAccountIfNeeded()
})
watch(chainId, async (newId, oldId) => {
  const fromChain_ = getChainFromId(chainId.value)
  if (!fromChain_) {
    const fallbackId = 56
    console.warn('[hieswap] unsupported chain id, fallback to BSC:', chainId.value)
    if (chainId.value !== fallbackId) setChainId(fallbackId)
    return
  }
  db.set('chainId', chainId.value)
  if(fromChain_.chainId == toChain.value.chainId){
    const savedId = Number(db.get('defToChainId', 0))
    const savedChain =
      savedId && getChainFromId(savedId) && savedId !== fromChain_.chainId
        ? getChainFromId(savedId)
        : null
    const toChian_ = savedChain || (fromChain_.chainId == 201022 ? Chains.bsc : Chains.fsc)
    setToChain(toChian_)
  }
  setFromChain(fromChain_)
  if (oldId != null && newId !== oldId && !String(account.value || '').trim()) {
    try {
      const { invalidateWcShellIfChainMismatch } = await import('./dapp/walletconnect.mjs')
      await invalidateWcShellIfChainMismatch(newId)
    } catch (_e) {
      void _e
    }
  }
})
export const refreshInfo = async () => {
  if(!account.value || !fromChain.value.chainId || !toChain.value.chainId) return;
  const fromBalance = await fetchErcBalanceResilient(fromChain.value.chainId, account.value)
  setFromBalance(bn(fromBalance))
  const allowance = await getAllowance(fromChain.value.chainId, account.value)
  setAllowance(bn(allowance))
  const exchangeAllowance = await getAllowance(fromChain.value.chainId, account.value, fromChain.value.exchangeContract)
  setExchangeAllowance(bn(exchangeAllowance))
  const toCid = toChain.value.chainId
  const naive = crossType.value === 'naive'
  if (naive) {
    const tn = await fetchNaiveBalanceResilient(toCid, account.value)
    if (toChain.value.chainId === toCid) {
      setToBalance(bn(0))
      setToNaiveBalance(tn)
    }
  } else {
    const [tb, tn] = await Promise.all([
      fetchErcBalanceResilient(toCid, account.value),
      fetchNaiveBalanceResilient(toCid, account.value),
    ])
    if (toChain.value.chainId === toCid) {
      setToBalance(bn(tb))
      setToNaiveBalance(tn)
    }
  }
  if (toChain.value.chainId === toCid) {
    setToChainGasPrice(await getGasPriceWithRetry(toCid, 3))
  }
}

const [showToast, setShowToast] = useShowToast()
export const toastMsg = ref('')
export const toastTone = ref('info')

/** 复制成功 — 各语言译文（与 data.mjs「复制成功」行一致） */
export const TOAST_COPY_SUCCESS_MSGS = new Set([
  '复制成功', 'Copied', '複製成功', 'コピー済み', '복사됨',
  'Copiado', 'Đã sao chép', 'Copié',
])

export const toast = (v, options = {}) => {
  let raw = v
  if (v instanceof Error) raw = v.message
  else if (v && typeof v === 'object' && v.message != null) raw = v.message
  else if (typeof v !== 'string') raw = String(v)
  const msg = humanizeWalletError(raw, lang.value)
  toastMsg.value = msg
  const tone = options.tone
    || (TOAST_COPY_SUCCESS_MSGS.has(String(raw).trim()) || TOAST_COPY_SUCCESS_MSGS.has(msg) ? 'success' : 'info')
  toastTone.value = tone
  setShowToast(true)
}

const TRON_WALLET_KEYS_BOOT = new Set(['tronLink', 'metamask', 'tokenPocket', 'bitget', 'ave', 'binance', 'okx', 'walletConnect'])

if (typeof window !== 'undefined') {
  sanitizeMobileWalletBoot()
  hydrateWalletSessionFromDb()
  whenWalletRailReady({ setChainId, getChainId: () => Number(chainId.value) }).then(() => {
    const chainNav = consumeChainNavigationPending()
    const savedChainId = Number(db.get('chainId', 0))
    const wantsTron =
      walletRail.value === 'tron'
      || isTronChainPageUrl(window.location.search)
      || (walletRail.value !== 'evm' && chainNav?.toChainId === TRON_CHAIN_ID)
    if (wantsTron && !isTronChainPageUrl(window.location.search)) {
      const target = buildFromChainPageUrl(TRON_CHAIN_ID)
      try {
        if (window.history?.replaceState) {
          window.history.replaceState(window.history.state, '', target)
        } else {
          window.location.replace(target)
        }
      } catch (_e) {
        window.location.replace(target)
      }
      if (Number(chainId.value) !== TRON_CHAIN_ID) setChainId(TRON_CHAIN_ID)
    }
    if (wantsTron) {
      const savedKey = String(db.get('wallet_key', '') || '').trim()
      if (savedKey && !TRON_WALLET_KEYS_BOOT.has(savedKey)) {
        applyWalletSession(resolveTronWalletSessionKey(savedKey, provider.value))
        db.set('wallet_type', 'inject')
        db.set('topic', '')
      }
      void syncTronWalletContext()
    }
  })
}